import { useMemo, useState } from "react";
import { relievingPressureKpaAbs, type ValveType } from "../calc/gas";
import { sizeTwoPhase, type TwoPhaseSizingInput } from "../calc/twoPhase";
import { selectOrificeWithParallel } from "../calc/orifice";
import { Field, Item, VALVE_LABELS, num } from "./formBits";

interface FormState {
  flow: string;
  setPressure: string;
  overpressure: string;
  backpressure: string;
  v1: string;
  v9: string;
  valveType: ValveType;
  kb: string;
  kc: string;
  kv: string;
  useApprox: boolean;
}

/** 초기값은 API 520 Part I Annex C.2.2.2 예제 조건이다. 결과는 T 오리피스 2개 병렬. */
const INITIAL: FormState = {
  flow: "216560",
  setPressure: "413.7", // 60 psig
  overpressure: "10",
  backpressure: "204.7", // 15 psig = 29.7 psia
  v1: "0.01945",
  v9: "0.02265",
  valveType: "balanced-bellows",
  kb: "1.0",
  kc: "1.0",
  kv: "1.0",
  useApprox: false,
};

/** 입구 조건에 따라 Annex C 의 방법이 갈린다. */
export type TwoPhaseInletKind = "two-phase" | "subcooled";

export function TwoPhaseSizingPanel() {
  const [f, setF] = useState<FormState>(INITIAL);

  const set =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) =>
      setF((prev) => ({ ...prev, [key]: value }));

  const p1 = useMemo(
    () => relievingPressureKpaAbs(num(f.setPressure), num(f.overpressure)),
    [f.setPressure, f.overpressure],
  );

  const sizing = useMemo(() => {
    const kb = f.kb.trim() === "" ? undefined : num(f.kb);
    const input: TwoPhaseSizingInput = {
      requiredFlowKgH: num(f.flow),
      relievingPressureKpaAbs: p1,
      backpressureKpaAbs: num(f.backpressure),
      inletSpecificVolumeM3Kg: num(f.v1),
      flashSpecificVolumeM3Kg: num(f.v9),
      valveType: f.valveType,
      combinationCorrection: num(f.kc),
      viscosityCorrection: num(f.kv),
      useApproximateCriticalRatio: f.useApprox,
      ...(kb === undefined ? {} : { bellowsBackpressureCorrection: kb }),
    };
    return sizeTwoPhase(input);
  }, [f, p1]);

  const selection = useMemo(
    () => (sizing.ok ? selectOrificeWithParallel(sizing.requiredAreaMm2) : null),
    [sizing],
  );

  const flashPressure = p1 * 0.9;

  return (
    <section className="card">
      <h2>2상 유동 사이징 (Omega 법)</h2>
      <p className="hint">
        API 520 Part I 10판 Annex C.2.2. PRV 입구가 이미 2상인 경우에 씁니다.
        <strong> 비체적 두 점</strong>이 필요합니다 — 입구 조건에서의 v₁ 과, 입구
        압력의 90 %({flashPressure.toFixed(1)} kPa)에서 플래시 계산한 v₉ 입니다.
        공정 시뮬레이터에서 얻어 입력하십시오. 초기값은 표준서 예제 조건입니다.
      </p>

      <div className="grid-form">
        <Field label="소요 유량 W" unit="kg/h" help="flowMass">
          <input value={f.flow} onChange={(e) => set("flow")(e.target.value)} />
        </Field>
        <Field label="설정압" unit="kPag" help="setPressure">
          <input
            value={f.setPressure}
            onChange={(e) => set("setPressure")(e.target.value)}
          />
        </Field>
        <Field label="허용 과압" unit="%" help="overpressure">
          <input
            value={f.overpressure}
            onChange={(e) => set("overpressure")(e.target.value)}
          />
        </Field>
        <Field label="총 배압 P2" unit="kPa abs" help="backpressureAbs">
          <input
            value={f.backpressure}
            onChange={(e) => set("backpressure")(e.target.value)}
          />
        </Field>
        <Field label="입구 비체적 v₁" unit="m³/kg" help="inletSpecificVolume">
          <input value={f.v1} onChange={(e) => set("v1")(e.target.value)} />
        </Field>
        <Field label="플래시 비체적 v₉" unit="m³/kg (P₁의 90 %)" help="flashSpecificVolume">
          <input value={f.v9} onChange={(e) => set("v9")(e.target.value)} />
        </Field>
        <Field label="밸브 형식" unit="" help="valveType">
          <select
            value={f.valveType}
            onChange={(e) => set("valveType")(e.target.value as ValveType)}
          >
            {(Object.keys(VALVE_LABELS) as ValveType[]).map((t) => (
              <option key={t} value={t}>
                {VALVE_LABELS[t]}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="배압 보정 Kb"
          unit={f.valveType === "balanced-bellows" ? "Figure 31 / 제작사" : "해당 없음"}
          help="kbBellows"
        >
          <input
            value={f.kb}
            disabled={f.valveType !== "balanced-bellows"}
            onChange={(e) => set("kb")(e.target.value)}
          />
        </Field>
        <Field label="점도 보정 Kv" unit="100 cP 이하면 1.0" help="kvTwoPhase">
          <input value={f.kv} onChange={(e) => set("kv")(e.target.value)} />
        </Field>
        <Field label="파열판 조합 Kc" unit="없으면 1.0" help="kc">
          <input value={f.kc} onChange={(e) => set("kc")(e.target.value)} />
        </Field>
        <Field label="임계 압력비 산출" unit="" help="criticalRatioMethod">
          <select
            value={f.useApprox ? "approx" : "exact"}
            onChange={(e) => set("useApprox")(e.target.value === "approx")}
          >
            <option value="exact">정의식 (C.14) 수치해</option>
            <option value="approx">근사식 (C.15)</option>
          </select>
        </Field>
      </div>

      {!sizing.ok ? (
        <div className="result result--error" role="alert">
          {sizing.message}
        </div>
      ) : (
        <div className="result result--ok">
          <div className="result__headline">
            <span className="result__label">소요 면적</span>
            <strong className="result__value">
              {sizing.requiredAreaMm2.toLocaleString("ko-KR", {
                maximumFractionDigits: 0,
              })}
            </strong>
            <span className="result__label">mm²</span>
            <span className="badge">
              {sizing.flowRegime === "critical" ? "임계유동" : "아임계유동"}
            </span>
          </div>

          {selection?.ok && (
            <div className="result__headline" style={{ marginTop: 10 }}>
              <span className="result__label">선정 오리피스</span>
              <strong className="result__value">
                {selection.orifice.designation}
              </strong>
              {selection.valveCount > 1 && (
                <span className="badge">{selection.valveCount}개 병렬</span>
              )}
              <span className="muted">
                여유율 {selection.marginPct.toFixed(1)} %
              </span>
            </div>
          )}
          {selection && !selection.ok && (
            <p className="muted" style={{ marginTop: 10 }}>
              {selection.message}
            </p>
          )}

          <h3>중간값 (계산서 기재용)</h3>
          <dl className="result__grid">
            <Item label="적용 식" value={sizing.equation} />
            <Item
              label="오메가 ω (식 C.12)"
              value={sizing.intermediates.omega.toFixed(4)}
            />
            <Item
              label={`임계압력비 ηc (${sizing.intermediates.criticalRatioEquation.replace("API 520 Part I ", "식 ")})`}
              value={sizing.intermediates.criticalPressureRatio.toFixed(4)}
            />
            <Item
              label="임계압력 Pcf (식 C.13)"
              value={`${sizing.intermediates.criticalPressureKpaAbs.toLocaleString("ko-KR", { maximumFractionDigits: 1 })} kPa abs`}
            />
            <Item
              label="배압비 ηa = P2/P1"
              value={sizing.intermediates.backpressureRatio.toFixed(4)}
            />
            <Item
              label={`질량유속 G (${sizing.intermediates.massFluxEquation.replace("API 520 Part I ", "식 ")})`}
              value={`${sizing.intermediates.massFlux.toLocaleString("ko-KR", { maximumFractionDigits: 0 })} kg/s·m²`}
            />
            <Item
              label="입구 압력 P1"
              value={`${p1.toLocaleString("ko-KR", { maximumFractionDigits: 1 })} kPa abs`}
            />
            <Item label="Kd" value={sizing.intermediates.kd.toFixed(3)} />
            <Item label="Kb" value={sizing.intermediates.kb.toFixed(3)} />
            <Item label="Kc" value={sizing.intermediates.kc.toFixed(3)} />
            <Item label="Kv" value={sizing.intermediates.kv.toFixed(3)} />
          </dl>

          {sizing.warnings.length > 0 && (
            <ul className="warnings">
              {sizing.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
